/**
 * ProposalComments Container Component
 *
 * Story BL-024.2: Governance-Suite Comment Thread UI
 * Task 6: Create ProposalComments container component
 *
 * Top-level container that wires up comment data fetching, auth state,
 * optimistic updates, and renders the comment thread UI.
 *
 * @module components/governance/ProposalComments
 */

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, useRoles } from '@hello-world-dao/auth';
import type { CommentThread, Comment as CommentType } from '../../types/proposalComments';
import {
  listComments,
  postComment,
  editComment,
  deleteComment,
  voteComment,
} from '../../services/proposalCommentService';
import { CommentForm } from './CommentForm';
import { CommentList } from './CommentList';

// =============================================================================
// Types
// =============================================================================

export interface ProposalCommentsProps {
  /** The proposal ID to load comments for */
  proposalId: number;
}

// =============================================================================
// Component
// =============================================================================

export function ProposalComments({ proposalId }: ProposalCommentsProps) {
  const { isAuthenticated, user } = useAuth();
  const { hasRole } = useRoles();

  const currentUserId = user?.userId;
  const isModeratorOrAdmin = hasRole('admin') || hasRole('moderator');

  // State
  const [comments, setComments] = useState<CommentThread[]>([]);
  const [totalTopLevel, setTotalTopLevel] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [sort, setSort] = useState<'created_asc' | 'score_desc'>('created_asc');

  // =========================================================================
  // Data Fetching
  // =========================================================================

  const fetchComments = useCallback(
    async (sortParam: 'created_asc' | 'score_desc') => {
      setIsLoading(true);
      try {
        const data = await listComments(proposalId, sortParam);
        setComments(data.comments);
        setTotalTopLevel(data.total_top_level);
      } catch {
        // Silently handle — empty state will show
        setComments([]);
        setTotalTopLevel(0);
      } finally {
        setIsLoading(false);
      }
    },
    [proposalId],
  );

  useEffect(() => {
    fetchComments(sort);
  }, [fetchComments, sort]);

  // =========================================================================
  // Sort
  // =========================================================================

  const handleSortChange = (newSort: 'created_asc' | 'score_desc') => {
    if (newSort !== sort) {
      setSort(newSort);
    }
  };

  // =========================================================================
  // Optimistic: Post top-level comment
  // =========================================================================

  const handlePostComment = async (content: string) => {
    const newComment = await postComment(proposalId, content);
    const thread: CommentThread = { ...newComment, replies: [] };
    setComments((prev) => [thread, ...prev]);
    setTotalTopLevel((prev) => prev + 1);
  };

  // =========================================================================
  // Optimistic: Reply
  // =========================================================================

  const handleReply = async (parentId: string, content: string) => {
    const newReply = await postComment(proposalId, content, parentId);
    setComments((prev) =>
      prev.map((c) =>
        c.id === parentId
          ? { ...c, replies: [...c.replies, newReply] }
          : c,
      ),
    );
  };

  // =========================================================================
  // Optimistic: Vote
  // =========================================================================

  const handleVote = async (commentId: string, vote: 1 | -1) => {
    // Find which comment/reply to update
    const snapshot = comments.map((c) => ({
      ...c,
      replies: [...c.replies],
    }));

    // Optimistic update helper
    const applyVote = (c: CommentType | CommentThread): CommentType | CommentThread => {
      if (c.id !== commentId) return c;
      const isToggle = c.userVote === vote;
      const newUserVote = isToggle ? 0 : vote;
      let upvotes = c.upvotes;
      let downvotes = c.downvotes;

      // Remove old vote
      if (c.userVote === 1) upvotes--;
      if (c.userVote === -1) downvotes--;
      // Apply new vote
      if (newUserVote === 1) upvotes++;
      if (newUserVote === -1) downvotes++;

      return {
        ...c,
        upvotes,
        downvotes,
        score: upvotes - downvotes,
        userVote: newUserVote as -1 | 0 | 1,
      };
    };

    setComments((prev) =>
      prev.map((c) => {
        const updated = applyVote(c) as CommentThread;
        return {
          ...updated,
          replies: c.replies.map((r) => applyVote(r) as CommentType),
        };
      }),
    );

    try {
      const serverCounts = await voteComment(proposalId, commentId, vote);
      // Sync with server values
      setComments((prev) =>
        prev.map((c) => {
          if (c.id === commentId) {
            return { ...c, ...serverCounts };
          }
          return {
            ...c,
            replies: c.replies.map((r) =>
              r.id === commentId ? { ...r, ...serverCounts } : r,
            ),
          };
        }),
      );
    } catch {
      // Revert on error
      setComments(snapshot);
    }
  };

  // =========================================================================
  // Optimistic: Edit
  // =========================================================================

  const handleEdit = async (commentId: string, content: string) => {
    const snapshot = comments.map((c) => ({
      ...c,
      replies: [...c.replies],
    }));

    const now = new Date().toISOString();
    setComments((prev) =>
      prev.map((c) => {
        if (c.id === commentId) {
          return { ...c, content, updatedAt: now };
        }
        return {
          ...c,
          replies: c.replies.map((r) =>
            r.id === commentId ? { ...r, content, updatedAt: now } : r,
          ),
        };
      }),
    );

    try {
      const updated = await editComment(proposalId, commentId, content);
      setComments((prev) =>
        prev.map((c) => {
          if (c.id === commentId) {
            return { ...c, content: updated.content, updatedAt: updated.updatedAt };
          }
          return {
            ...c,
            replies: c.replies.map((r) =>
              r.id === commentId
                ? { ...r, content: updated.content, updatedAt: updated.updatedAt }
                : r,
            ),
          };
        }),
      );
    } catch {
      setComments(snapshot);
    }
  };

  // =========================================================================
  // Optimistic: Delete
  // =========================================================================

  const handleDelete = async (commentId: string) => {
    const snapshot = comments.map((c) => ({
      ...c,
      replies: [...c.replies],
    }));

    setComments((prev) =>
      prev.map((c) => {
        if (c.id === commentId) {
          return { ...c, isDeleted: true, content: '[deleted]' };
        }
        return {
          ...c,
          replies: c.replies.map((r) =>
            r.id === commentId
              ? { ...r, isDeleted: true, content: '[deleted]' }
              : r,
          ),
        };
      }),
    );

    try {
      await deleteComment(proposalId, commentId);
    } catch {
      setComments(snapshot);
    }
  };

  // =========================================================================
  // Render
  // =========================================================================

  return (
    <section className="mt-8" data-testid="proposal-comments-section">
      {/* Header with Discussion count and sort toggle */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold" data-testid="discussion-heading">
          Discussion ({totalTopLevel})
        </h2>
        <div className="flex gap-2" data-testid="sort-controls">
          <button
            type="button"
            onClick={() => handleSortChange('created_asc')}
            className={`text-sm px-3 py-1 rounded-md ${
              sort === 'created_asc'
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:text-foreground'
            }`}
            data-testid="sort-newest"
          >
            Newest first
          </button>
          <button
            type="button"
            onClick={() => handleSortChange('score_desc')}
            className={`text-sm px-3 py-1 rounded-md ${
              sort === 'score_desc'
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:text-foreground'
            }`}
            data-testid="sort-top-rated"
          >
            Top rated
          </button>
        </div>
      </div>

      {/* Comment form or sign-in prompt */}
      {isAuthenticated ? (
        <div className="mb-6" data-testid="comment-form-section">
          <CommentForm
            onSubmit={handlePostComment}
            placeholder="Add a comment…"
          />
        </div>
      ) : (
        <div
          className="mb-6 p-4 rounded-lg bg-muted text-center"
          data-testid="sign-in-prompt"
        >
          <Link to="/login" className="text-primary hover:underline">
            Sign in to comment
          </Link>
        </div>
      )}

      {/* Comment list */}
      <CommentList
        comments={comments}
        isLoading={isLoading}
        currentUserId={currentUserId}
        isAuthenticated={isAuthenticated}
        isModeratorOrAdmin={isModeratorOrAdmin}
        onVote={handleVote}
        onReply={handleReply}
        onDelete={handleDelete}
        onEdit={handleEdit}
        emptyMessage={
          sort === 'score_desc'
            ? 'No highly-rated comments yet.'
            : 'Be the first to comment on this proposal.'
        }
      />
    </section>
  );
}

export default ProposalComments;
